import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ChevronDown, Search as SearchIcon, X } from 'lucide-react';

// Shared left vertical settings rail, used by both CompanySettings and
// UserSettings. Renders the (already feature-filtered) sections, each linking
// to `${basePath}/${slug}` so sections are deep-linkable, and highlights the
// active one. Sections may optionally carry a `group` label — if any do, items
// are rendered under collapsible group headers; otherwise the rail is a flat
// list.
//
// Sections may also carry `keywords: []` — extra search terms that do not
// appear in the label. "Moms" should find Företagsuppgifter (momsstatus lives
// there) even though the word is not in the rail label.

const COLLAPSED_STORAGE_KEY = 'll.settings.railCollapsed';

// Search is navigation aid, not decoration: below this many items the rail is
// scannable at a glance and a search box is just another thing to look at.
const SEARCH_MIN_ITEMS = 8;

// Fold diacritics and case so "bokforing" matches "Bokföring" — Swedish labels
// are full of å/ä/ö and nobody reaches for them mid-search.
const fold = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// Collapse state: the user's choice if they have made one, otherwise the
// caller's defaults. The set-once groups (integrations, AI, sales/payroll)
// start collapsed so the rail stays roughly one screen tall — with 19 leaves
// an all-expanded rail scrolls inside itself, which is its own small maze.
// Search still reaches every section regardless of what is collapsed.
const readCollapsed = (defaults) => {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set(defaults);
  } catch {
    return new Set(defaults);
  }
};

const SettingsRail = ({ basePath, activeSlug, sections, defaultCollapsedGroups = [] }) => {
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState(() => readCollapsed(defaultCollapsedGroups));
  // Index into the flattened visible list, for arrow-key navigation. -1 = none.
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef(null);

  const hasGroups = sections.some((s) => s.group);
  const showSearch = sections.length >= SEARCH_MIN_ITEMS;

  // Filtered sections. Empty query = everything, so the rail is unchanged for
  // anyone who never types in it.
  const matches = useMemo(() => {
    const q = fold(query.trim());
    if (!q) return sections;
    return sections.filter((s) => {
      const haystack = [s.label, s.group, ...(s.keywords || [])].map(fold);
      return haystack.some((h) => h.includes(q));
    });
  }, [sections, query]);

  // Build groups preserving first-seen order. With no groups, everything goes
  // into a single untitled group (flat list).
  const groups = useMemo(() => {
    if (!hasGroups) return [{ title: null, items: matches }];
    const out = [];
    matches.forEach((s) => {
      let group = out.find((g) => g.title === s.group);
      if (!group) {
        group = { title: s.group, items: [] };
        out.push(group);
      }
      group.items.push(s);
    });
    return out;
  }, [matches, hasGroups]);

  // A group is open unless the user collapsed it — except while searching (the
  // results are the point) and for the group holding the active section, which
  // must never hide where you currently are.
  const searching = query.trim().length > 0;
  const activeGroup = sections.find((s) => s.slug === activeSlug)?.group || null;
  const isOpen = (title) =>
    !title || searching || title === activeGroup || !collapsed.has(title);

  const toggleGroup = (title) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      try {
        window.localStorage.setItem(
          COLLAPSED_STORAGE_KEY,
          JSON.stringify([...next])
        );
      } catch {
        /* private mode — collapse just won't persist */
      }
      return next;
    });
  };

  // The visible items in render order, so Up/Down walks what the eye sees.
  const visible = useMemo(
    () => groups.filter((g) => isOpen(g.title)).flatMap((g) => g.items),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, collapsed, searching, activeGroup]
  );

  // Typing restarts the walk at the first result.
  useEffect(() => {
    setHighlight(searching && visible.length ? 0 : -1);
  }, [query, searching, visible.length]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setQuery('');
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!visible.length) return;
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (h + step + visible.length) % visible.length);
      return;
    }
    if (e.key === 'Enter' && highlight >= 0 && visible[highlight]) {
      e.preventDefault();
      history.push(`${basePath}/${visible[highlight].slug}`);
      setQuery('');
    }
  };

  // `#main-wrapper` is `overflow: hidden`, which makes it the sticky element's
  // scrollport — and it never scrolls, so `position: sticky` on the rail was
  // inert and the rail scrolled off the top of a 35-screen page. Opt this one
  // wrapper out for as long as a settings page is mounted.
  useEffect(() => {
    const wrapper = document.getElementById('main-wrapper');
    if (!wrapper) return undefined;
    wrapper.classList.add('settings-sticky-host');
    return () => wrapper.classList.remove('settings-sticky-host');
  }, []);

  return (
    <nav className="settings-rail" aria-label="Inställningar">
      {showSearch && (
        <div className="settings-rail-search">
          <SearchIcon size={16} className="settings-rail-search-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Sök inställning"
            aria-label="Sök inställning"
            className="settings-rail-search-input"
          />
          {query && (
            <button
              type="button"
              className="settings-rail-search-clear"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              aria-label="Rensa sökning"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      <div className="settings-rail-groups">
        {groups.map((group, gi) => {
          const open = isOpen(group.title);
          return (
            <div
              className={`settings-rail-group${open ? '' : ' is-collapsed'}`}
              key={group.title || gi}
            >
              {group.title && (
                <button
                  type="button"
                  className="settings-rail-group-title"
                  onClick={() => toggleGroup(group.title)}
                  aria-expanded={open}
                >
                  <span>{group.title}</span>
                  <ChevronDown
                    className="settings-rail-group-chevron"
                    size={14}
                    aria-hidden="true"
                  />
                </button>
              )}
              <div className="settings-rail-group-items">
                {group.items.map((s) => {
                  const isHighlighted =
                    highlight >= 0 && visible[highlight]?.slug === s.slug;
                  return (
                    <Link
                      key={s.slug}
                      to={`${basePath}/${s.slug}`}
                      className={`settings-rail-item${
                        s.slug === activeSlug ? ' is-active' : ''
                      }${isHighlighted ? ' is-highlighted' : ''}`}
                      aria-current={s.slug === activeSlug ? 'page' : undefined}
                      onClick={() => setQuery('')}
                    >
                      <s.Icon className="settings-rail-icon" size={18} strokeWidth={1.75} />
                      <span className="settings-rail-label">{s.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {searching && !matches.length && (
        <p className="settings-rail-empty">Inga träffar för ”{query.trim()}”</p>
      )}
    </nav>
  );
};

export default SettingsRail;
